// Port of src/app/interceptors/custom-http.interceptor.ts as a configured
// axios instance with request/response interceptors.
//
// Behavior replicated function-by-function:
//  - connection-quality checks via navigator.connection (effectiveType /
//    RTT > 500ms / downlink < 1.0 Mbps), throttled to once per 10s, with
//    once-per-type warnings and a 5s toast cooldown (Angular TOAST_COOLDOWN=5000);
//  - retry up to 3 times with a 1s delay on network errors (Angular: status 0);
//  - recovery for mypart URLs: on any error, retry the original request once —
//    logging in the default user first when no user is logged in
//    (the Angular `completeRequestWithRecovery` / `hasAttemptedRecovery` flow);
//  - 511 → reload (default user) or LOGIN_REGISTER modal;
//  - critical errors (status 0/500/503) → toast + navigate home
//    (setShowBigSearchHomePage);
//  - loader integration: useLoaderStore.show()/hide() tracking an active
//    request count. Requests marked `loaderIgnored: true` (the port of
//    Angular's eventHttpHeaders / NGX_LOADING_BAR_IGNORED context) skip the
//    loader entirely.
//
// NOTE(port): Angular kept `hasAttemptedRecovery` as a single instance-level
// flag shared by all in-flight requests; here the recovery/retry counters live
// on the per-request config (the axios-retry pattern), which matches the
// intended per-request pipeline semantics.

import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { environment } from '../config/environment';
import { ModalType } from '../interfaces/modal-types.enum';
import { useLoaderStore } from '../stores/loader-store';
import { useToastStore } from '../stores/toast-store';
import { useModalStore } from '../stores/modal-store';
import { useUserStore } from '../stores/user-store';
import { useGlobalStore } from '../stores/global-store';
import { loginDefaultUser } from './http-req';
import { logger } from '../shared/logging/logger';
import { redactUrlEncoded } from '../shared/logging/redact';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Port of Angular's `eventHttpHeaders` NGX_LOADING_BAR_IGNORED context:
     * requests flagged here do not trigger the global loader.
     */
    loaderIgnored?: boolean;
  }
}

interface NetworkInformation extends EventTarget {
  readonly effectiveType: '4g' | '3g' | '2g' | 'slow-2g';
  readonly downlink: number;
  readonly rtt: number;
  readonly saveData: boolean;
  onchange: EventListener;
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformation;
}

enum ConnectionWarningType {
  VERY_SLOW = 'very_slow',
  SLOW = 'slow',
  DEGRADED = 'degraded'
}

interface RetryStateConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
  __recoveryAttempted?: boolean;
  __logStart?: number;
}

// --- interceptor instance state (Angular: private class fields) ---
let activeRequests = 0;
let lastConnectionCheck = 0;
const connectionCheckInterval = 10000; // Check connection every 10 seconds at most
const SLOW_RTT_THRESHOLD = 500; // Round trip time threshold in ms
const SLOW_DOWNLINK_THRESHOLD = 1.0; // Downlink threshold in Mbps
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const shownWarnings = new Set<ConnectionWarningType>();
let lastToastTime = 0;
const TOAST_COOLDOWN = 5000; // 5 seconds between toasts

export const http = axios.create();

http.interceptors.request.use((config) => {
  (config as RetryStateConfig).__logStart = Date.now();

  if (!config.loaderIgnored) {
    useLoaderStore.getState().show();
    activeRequests++;
  }

  // Skip connection check for certain endpoints
  if (!shouldSkipConnectionCheck(config.url || '')) {
    if (!checkConnectionQuality()) {
      // Instead of throwing error immediately, try to proceed with the request
      // but show a warning to the user
      showConnectionWarning();
    }
  }

  const rawContentType =
    config.headers && typeof (config.headers as any).get === 'function'
      ? (config.headers as any).get('Content-Type')
      : (config.headers as any)?.['Content-Type'];
  const loggedBody =
    typeof config.data === 'string' &&
    typeof rawContentType === 'string' &&
    rawContentType.includes('application/x-www-form-urlencoded')
      ? redactUrlEncoded(config.data)
      : config.data;

  logger.info('http', `${(config.method || 'get').toUpperCase()} ${config.url || ''}`, {
    params: config.params,
    body: loggedBody,
  });

  return config;
});

http.interceptors.response.use(
  (response: AxiosResponse) => {
    finalizeRequest(response.config);
    const start = (response.config as RetryStateConfig).__logStart;
    const ms = start ? Date.now() - start : undefined;
    logger.info(
      'http',
      `${(response.config.method || 'get').toUpperCase()} ${response.config.url || ''} → ${response.status}${ms != null ? ` (${ms}ms)` : ''}`,
      { data: response.data }
    );
    return response;
  },
  async (error: unknown) => {
    const axiosError = error as AxiosError;
    const config = axiosError?.config as RetryStateConfig | undefined;

    if (config) {
      finalizeRequest(config);
    }

    // Aborted requests (cancelRequests / AbortController) bypass retry,
    // recovery and error toasts — Angular's takeUntil-cancelled requests never
    // hit the interceptor's catchError either.
    if (axios.isCancel(error) || !config || !axiosError.isAxiosError) {
      throw error;
    }

    const url = config.url || '';

    // Angular: skip-connection-check requests went through
    // completeRequestNoRecovery with no retry / recovery / error handling.
    if (shouldSkipConnectionCheck(url)) {
      throw error;
    }

    const status = axiosError.response?.status ?? 0;

    const logStart = config.__logStart;
    const logMs = logStart ? Date.now() - logStart : undefined;
    logger.error(
      'http',
      `${(config.method || 'get').toUpperCase()} ${url} → ${status}${logMs != null ? ` (${logMs}ms)` : ''}`,
      {
        message: axiosError.message,
        responseData: axiosError.response?.data,
        retryCount: config.__retryCount,
        recoveryAttempted: config.__recoveryAttempted,
      }
    );

    // --- recovery (Angular: checkIfNeedsRecovery / completeRequestWithRecovery) ---
    // In dev the API URLs are relative ('/mypart-webapp/...', proxied by Vite),
    // so match those too — Angular only ever saw absolute mypart.com URLs.
    const recoveryApplies =
      (url.includes('localhost:4200') || url.includes('mypart.com') || url.includes('/mypart-webapp/'))
      && !url.includes('mypart-webapp/register');

    // Don't recover from payload too large errors - let them propagate
    const isPayloadTooLarge = (axiosError.response?.data as any)?.error?.code === 'PAYLOAD_TOO_LARGE';

    if (recoveryApplies && !isPayloadTooLarge && !config.__recoveryAttempted) {
      config.__recoveryAttempted = true;

      if (useUserStore.getState().user) {
        return http.request(config);
      }

      let loginSucceeded = true;
      try {
        await loginDefaultUser();
      } catch {
        // Angular mapped a failed default-login back to the ORIGINAL error.
        loginSucceeded = false;
      }
      if (loginSucceeded) {
        return http.request(config);
      }
    }

    // --- retry (Angular: retry({ count: 3, delay: 1s }) on status 0 only) ---
    if (status === 0 && (config.__retryCount ?? 0) < MAX_RETRIES) {
      config.__retryCount = (config.__retryCount ?? 0) + 1;
      config.__recoveryAttempted = false; // each retry re-runs the full recovery chain
      await delay(RETRY_DELAY);
      return http.request(config);
    }

    // --- terminal error handling (Angular: outer catchError) ---
    if (status === 0) {
      showConnectionError();
    }
    handleServerRestartError(status, axiosError);
    handleCriticalErrors(status);

    throw error;
  }
);

/**
 * Port of jquery-param (`param()` from the Angular service). Produces the same
 * x-www-form-urlencoded output: nested objects as `a[b]=c`, arrays as `a[]=v`,
 * spaces encoded as `+`.
 */
export function param(obj: Record<string, any>): string {
  const s: string[] = [];

  const add = (key: string, value: any) => {
    value = typeof value === 'function' ? value() : value;
    value = value === null || value === undefined ? '' : value;
    s.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
  };

  const buildParams = (prefix: string, value: any) => {
    if (Array.isArray(value)) {
      value.forEach((v, i) => {
        if (/\[\]$/.test(prefix)) {
          add(prefix, v);
        } else {
          buildParams(prefix + '[' + (typeof v === 'object' && v !== null ? i : '') + ']', v);
        }
      });
    } else if (value !== null && typeof value === 'object') {
      for (const name of Object.keys(value)) {
        buildParams(prefix + '[' + name + ']', value[name]);
      }
    } else {
      add(prefix, value);
    }
  };

  for (const name of Object.keys(obj)) {
    buildParams(name, obj[name]);
  }

  return s.join('&').replace(/%20/g, '+');
}

/**
 * Port of Angular's `httpPostOptions` POSTs: x-www-form-urlencoded body
 * (serialized with jquery-param semantics) + withCredentials. Resolves with
 * the full axios response (Angular used `observe: 'response'`).
 */
export function postUrlencoded<T = any>(
  url: string,
  body: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return http.post<T>(url, param(body), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    withCredentials: true,
    ...config,
  });
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function shouldSkipConnectionCheck(url: string): boolean {
  // Skip connection check for essential resources or specific endpoints
  return url.includes('/assets/') ||
    url.includes('favicon.ico') ||
    url.includes('manifest.json');
}

function showConnectionWarning(): void {
  const now = Date.now();
  if (now - lastToastTime >= TOAST_COOLDOWN) {
    useToastStore.getState().warning('Poor connection detected - some features may be limited', 5000);
    lastToastTime = now;
  }
}

function showConnectionError(): void {
  const now = Date.now();
  if (now - lastToastTime >= TOAST_COOLDOWN) {
    useToastStore.getState().error('Connection error - retrying...', 5000);
    lastToastTime = now;
  }
}

function finalizeRequest(config: AxiosRequestConfig | undefined): void {
  if (config?.loaderIgnored) {
    return;
  }
  activeRequests--;
  if (activeRequests <= 0) {
    activeRequests = 0;
    useLoaderStore.getState().hide();
  }
}

function showWarningOnce(type: ConnectionWarningType, message: string): void {
  const now = Date.now();
  if (!shownWarnings.has(type) && now - lastToastTime >= TOAST_COOLDOWN) {
    useToastStore.getState().warning(message, 5000);
    shownWarnings.add(type);
    lastToastTime = now;
  }
}

function checkConnectionQuality(): boolean {
  const now = Date.now();
  if (now - lastConnectionCheck < connectionCheckInterval) {
    return true; // Skip check if we checked recently
  }
  lastConnectionCheck = now;

  // Use modern Network Information API if available
  const nav = navigator as NavigatorWithConnection;
  if (nav.connection) {
    const conn = nav.connection;

    // Check effective connection type
    if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') {
      showWarningOnce(
        ConnectionWarningType.VERY_SLOW,
        'Very slow connection detected - some features may be limited'
      );
      return true; // Allow but warn
    }

    // Check round trip time and downlink speed
    if (conn.rtt > SLOW_RTT_THRESHOLD || conn.downlink < SLOW_DOWNLINK_THRESHOLD) {
      showWarningOnce(
        ConnectionWarningType.SLOW,
        'Slow connection detected - performance may be affected'
      );
      return true; // Allow but warn
    }

    // Add listener for connection changes
    conn.onchange = () => {
      if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') {
        showWarningOnce(
          ConnectionWarningType.DEGRADED,
          'Connection speed has degraded - please check your internet'
        );
      }
    };
  }

  // Always return true - we'll handle connection issues through error handling
  return true;
}

function handleServerRestartError(status: number, error: AxiosError): void {
  if (status === 511) {
    console.error('Error 511: Network Authentication Required', error);

    if (useUserStore.getState().user?.email === environment.defaultUserEmail) {
      window.location.reload();
    } else {
      useModalStore.getState().open(ModalType.LOGIN_REGISTER, {
        inputs: {
          loginPopupTitle: 'An issue has occurred, please sign in again',
          activateHardRefresh: true,
          hideCloseButton: true
        }
      });
    }
  }
}

function handleCriticalErrors(status: number): void {
  // Handle critical errors: network errors (0), server errors (500), and service unavailable (503)
  if (status === 0 || status === 500 || status === 503) {
    const now = Date.now();
    // Only show toast once per cooldown period to avoid spam
    if (now - lastToastTime >= TOAST_COOLDOWN) {
      useToastStore.getState().error('We\'re experiencing server issues. We\'re working on it.', 7000);
      lastToastTime = now;
    }

    // Navigate to home page
    useGlobalStore.getState().setShowBigSearchHomePage(true, 'CustomHttpInterceptor - Critical Error');
  }
}
